import { useEffect, useState } from 'react';
import { useWaysteClient } from '@alliance-disposal/client';
import type { Profile } from '@alliance-disposal/transport-types';
import { useAuth0 } from '@auth0/auth0-react';
import posthog from 'posthog-js';

export type Auth0AuthGate = {
    /**
     * True while Auth0 initializes, while an authenticated session is still exchanging for a Wayste authority, or
     * while a failed session is being signed out — shells keep their loader up rather than rendering a login route.
     */
    isResolving: boolean;
    isAuthenticated: boolean;
    authority: Profile.AuthorityTransport | null;
    /** Auth0 initialization/session failure. The gate signs the user out; exposed so shells can show a failure state. */
    error: Error | undefined;
    /** Auth0 sign-out that returns the browser to the app origin. */
    logoutWithRedirect: () => void;
};

/**
 * Wires an Auth0 session into the WaysteClient and reports when the app is safe to render.
 *
 * Hands the SDK's token accessors to Auth0IDTokenProvider (which owns ID token freshness) and points signOut() at
 * the Auth0 logout, then tracks the resulting authority. `isResolving` stays true through the token exchange —
 * without it the app renders its unauthenticated tree for a beat and route guards bounce a mid-exchange user
 * straight back to the hosted login. An Auth0-authenticated user who never resolves an authority is signed out by
 * AuthService itself, which clears `isAuthenticated` and ends the wait.
 *
 * Only call inside `<StandardAuth0Provider>` (useAuth0) and `<WaysteProvider>` (useWaysteClient).
 */
export const useAuth0AuthGate = (): Auth0AuthGate => {
    const client = useWaysteClient();
    const { isLoading: isAuth0Loading, isAuthenticated, error, logout, getIdTokenClaims, getAccessTokenSilently } = useAuth0();
    const [authority, setAuthority] = useState<Profile.AuthorityTransport | null>(null);

    const logoutWithRedirect = () => logout({ logoutParams: { returnTo: window.location.origin } });

    useEffect(() => {
        const sub = client.auth().currentAuthority.subscribe(setAuthority);

        return () => sub.unsubscribe();
    }, [client]);

    useEffect(() => {
        if (!error) return;
        // Has to be visible — an Auth0 failure otherwise only shows up as users reporting a login loop.
        console.error('Auth error', error);
        posthog.captureException(error);
        logoutWithRedirect();
    }, [error]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        // useAuth0() is only available inside the provider tree, so these are injected rather than imported.
        client.auth().registerAuthLifecycleHandlers({
            tokenAccessors: { getIDTokenClaims: getIdTokenClaims, getAccessTokenSilently },
            signOutHandler: logoutWithRedirect,
        });
    }, [isAuthenticated]);

    return {
        // `error` holds isResolving true so the shell doesn't render Auth0LoginRedirect between the failure and the
        // logout redirect landing — that would send the broken session straight back to the hosted login.
        isResolving: isAuth0Loading || Boolean(error) || (isAuthenticated && !authority),
        isAuthenticated,
        authority,
        error,
        logoutWithRedirect,
    };
};
